import { Alert, Button, Spin, Typography } from "antd";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { receiptAPIWithErrorHandling } from "../../../api/receipt/receipt";
import { DownloadOutlined } from "@ant-design/icons";

const Receipt = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [receiptData, setReceipt] = useState(null);
    const { Text } = Typography;

    const code = searchParams.get('code');

    useEffect(() => {
        if (!code) return;

        // ✅ check code bắt đầu bằng ONEDU_
        if (code.startsWith("ONEDU_")) {
          const id = code.replace("ONEDU_", ""); // hoặc code.split("_")[1]

          // navigate sang receipts kèm params id
          navigate(`/onedu?code=${id}`, { replace: true });
          return;
        }

        const loadPDF = async () => {
            try {
                const response = await receiptAPIWithErrorHandling.getReceipt(code);
                setReceipt(response.data || []);
            } catch (err) {
                setError(true);
            } finally {
                setLoading(false);
            }
        };

        if (code) loadPDF();

    }, [code]);

    if (loading) {
        return (
            <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '100vh' 
            }}>
                <Spin size="large" />
            </div>
        );
    }

    if (error || !code) {
      console.log('error', error)
        return (
            <div style={{ padding: '40px', textAlign: 'center' }}>
                <Alert
                  message="Vui lòng kiểm tra lại mã phiếu hoặc thử lại sau!"
                  type="error"
                  showIcon
                />
            </div>
        );
    }
    
    if (!receiptData || receiptData.length === 0) {
      return (
        <div style={{ padding: 40, textAlign: "center" }}>
          <Alert
            message="Vui lòng kiểm tra lại mã phiếu hoặc thử lại sau!"
            type="warning"
            showIcon
          />
        </div>
      );
    }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("vi-VN").format(amount) + " đồng";
  };

  // Có dòng đơn vị phát hành thì tách dòng đơn vị cấp 1 ra một nhịp, như phiếu PDF.
  const hasIssuer = Boolean(
    receiptData.parent_agency || receiptData.auth_org || receiptData.issuer_address || receiptData.tax_code
  );

  // Khối phải đầu phiếu: quốc hiệu - tiêu ngữ và/hoặc Mẫu số, theo cấu hình của chủ.
  const showNationalTitle = receiptData.show_national_title === true;
  const showFormCode = receiptData.show_form_code !== false;
  const hasRightBlock = showNationalTitle || showFormCode;

  // Backend không cấp QR khi không còn gì phải nộp; nói rõ lý do thay vì để trống chỗ QR.
  const isSettled = receiptData.status === "CANCELLED" || Number(receiptData.total) <= 0;

  const settledMessage = () => {
    if (receiptData.status === "CANCELLED") return "Khoản thu này đã được hủy, không cần thanh toán.";
    if (Number(receiptData.amount_paid) > 0) return "Khoản thu này đã được thanh toán đủ.";
    return "Khoản thu này không có số tiền phải nộp.";
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return {
      day: date.getDate(),
      month: date.getMonth() + 1,
      year: date.getFullYear(),
    };
  };

  const date = formatDate(receiptData.date);

  return (
    <div style={{ backgroundColor: "#F8FAFC", minHeight: "100vh", padding: "10px" }}>
      <style>{`
        * {
          box-sizing: border-box;
        }

        @media print {
          body * { visibility: hidden; }
          .print-area, .print-area * { visibility: visible; }
          .print-area {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            padding: 20px;
          }
          .no-print { display: none !important; }
        }

        .receipt-wrapper {
          background: white;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        .receipt-container {
          font-family: 'DejaVu Sans', Arial, sans-serif;
          padding: 16px;
          max-width: 600px;
          margin: 0 auto;
          line-height: 1.5;
          font-size: 13px;
        }

        /* Đầu phiếu theo bố cục phiếu PDF: tắt Mẫu số thì khối đơn vị phát hành chiếm hết
           chiều ngang, bật thì chia 3:2 để tên đơn vị không gãy dòng. */
        .header-section {
          display: grid;
          grid-template-columns: 1fr;
          gap: 16px;
          margin-bottom: 16px;
          padding-bottom: 12px;
          border-bottom: 2px solid #E2E8F0;
          font-size: 12px;
          line-height: 1.45;
        }

        .header-section.has-form-code {
          grid-template-columns: 3fr 2fr;
        }

        .header-left {
          text-align: left;
        }

        .header-right {
          text-align: right;
        }

        .issuer-name {
          font-weight: 700;
          font-size: 13px;
        }

        .issuer-unit {
          margin-top: 4px;
        }

        /* Quốc hiệu - tiêu ngữ canh giữa khối phải, tiêu ngữ gạch chân như phiếu PDF. */
        .header-right.national {
          text-align: center;
        }

        .national-title {
          font-weight: 700;
          font-size: 12px;
        }

        .national-motto {
          border-bottom: 1px solid #333;
          padding-bottom: 1px;
        }

        .footer-note {
          margin-top: 16px;
          padding-top: 8px;
          border-top: 1px solid #E2E8F0;
          font-size: 11px;
          line-height: 1.5;
          color: #555;
          white-space: pre-line;
        }

        .title-section {
          text-align: center;
          margin: 20px 0 16px 0;
        }

        .receipt-title {
          font-size: 22px;
          font-weight: 700;
          margin: 0 0 8px 0;
          letter-spacing: 0.5px;
        }

        .receipt-date {
          font-size: 12px;
          margin: 4px 0 2px 0;
          color: #333;
        }

        .receipt-number {
          font-size: 12px;
          color: #666;
        }

        .info-section {
          margin: 16px 0;
          background: #f9f9f9;
          padding: 12px;
          border-radius: 6px;
        }

        .info-item {
          margin: 10px 0;
          display: flex;
          // flex-direction: column;
          justify-content: space-between;
        }

        .info-label {
          font-weight: 600;
          font-size: 12px;
          margin-bottom: 3px;
          color: #333;
        }

        .info-value {
          font-size: 13px;
          color: #555;
          word-break: break-word;
          line-height: 1.4;
        }

        .amount-highlight {
          background: #fff3cd;
          border-left: 4px solid #ff6b6b;
          padding: 10px 12px;
          margin: 12px 0;
          border-radius: 4px;
        }

        .amount-value {
          font-weight: 700;
          color: #E5065C;
          font-size: 16px;
        }

        .amount-text {
          font-size: 12px;
          margin-top: 6px;
          color: #333;
        }

        .qr-section {
          text-align: center;
          margin: 16px 0;
          padding: 12px 0;
          border: 1px dashed #CBD5E1;
          border-radius: 4px;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .qr-image {
          width: 100%;
          max-width: 280px;
          height: auto;
          border-radius: 4px;
        }

        .details-section {
          margin: 16px 0;
        }

        .details-title {
          font-weight: 600;
          font-size: 13px;
          margin-bottom: 10px;
          padding-bottom: 8px;
          border-bottom: 2px solid #E2E8F0;
        }

        .detail-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 0;
          border-bottom: 1px solid #F1F5F9;
          font-size: 12px;
        }

        .detail-row:last-child {
          border-bottom: none;
        }

        .detail-name {
          flex: 1;
          color: #333;
        }

        .detail-amount {
          font-weight: 600;
          color: #333;
          margin-left: 8px;
          text-align: right;
          min-width: 120px;
        }

        .summary-section {
          background: #f9f9f9;
          padding: 12px;
          border-radius: 6px;
          margin: 12px 0;
        }

        .summary-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 6px 0;
          font-size: 12px;
        }

        .summary-label {
          color: #666;
          font-weight: 500;
        }

        .summary-value {
          font-weight: 600;
          color: #333;
        }

        .summary-total {
          border-top: 2px solid #CBD5E1;
          padding-top: 8px;
          margin-top: 8px;
        }

        .summary-total .summary-label {
          font-weight: 700;
          color: #E5065C;
        }

        .summary-total .summary-value {
          font-weight: 700;
          color: #E5065C;
          font-size: 14px;
        }

        .signature-section {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
          margin-top: 32px;
          padding-top: 16px;
          border-top: 2px solid #E2E8F0;
        }

        .signature-box {
          text-align: center;
        }

        .signature-title {
          font-weight: 600;
          font-size: 12px;
          margin-bottom: 24px;
          color: #333;
        }

        .signature-line {
          font-size: 11px;
          color: #999;
          font-style: italic;
          margin-top: 32px;
        }

        .no-print {
          background: #0070D7;
          color: white;
          border: none;
          padding: 12px 16px;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 600;
          width: 100%;
          margin-bottom: 12px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          transition: background 0.3s;
        }

        .no-print:hover {
          background: #0050b3;
        }

        @media (max-width: 480px) {
          .receipt-container {
            padding: 12px;
            font-size: 12px;
          }

          .header-section {
            gap: 8px;
            margin-bottom: 12px;
            padding-bottom: 10px;
          }

          /* Màn hẹp: Mẫu số xuống dưới khối đơn vị thay vì ép hai cột chật. */
          .header-section.has-form-code {
            grid-template-columns: 1fr;
          }

          .header-right {
            text-align: left;
          }

          .issuer-name {
            font-size: 12px;
          }

          .receipt-title {
            font-size: 18px;
            margin-bottom: 6px;
          }

          .receipt-date, .receipt-number {
            font-size: 11px;
          }

          .info-section {
            padding: 10px;
            margin: 12px 0;
          }

          .info-item {
            margin: 8px 0;
          }

          .info-label {
            font-size: 11px;
            margin-bottom: 2px;
          }

          .info-value {
            font-size: 12px;
          }

          .amount-value {
            font-size: 15px;
          }

          .qr-image {
            max-width: 160px;
          }

          .detail-row {
            padding: 6px 0;
            font-size: 11px;
          }

          .detail-amount {
            min-width: 100px;
          }

          .summary-row {
            font-size: 11px;
            padding: 5px 0;
          }

          .signature-section {
            gap: 12px;
            margin-top: 24px;
            padding-top: 12px;
          }

          .signature-title {
            font-size: 11px;
            margin-bottom: 20px;
          }

          .signature-line {
            font-size: 10px;
            margin-top: 24px;
          }
        }
      `}</style>

      <div className="receipt-wrapper print-area">
        <div className="receipt-container">
          {/* Header - cùng nội dung với phiếu PDF (_receipt_body.blade.php), theo hồ sơ phiếu của chủ */}
          <div className={`header-section${hasRightBlock ? " has-form-code" : ""}`}>
            <div className="header-left">
              {receiptData.parent_agency && <div>{receiptData.parent_agency}</div>}
              {receiptData.auth_org && <div className="issuer-name">{receiptData.auth_org}</div>}
              {receiptData.issuer_address && <div>{receiptData.issuer_address}</div>}
              {receiptData.tax_code && <div>MST: {receiptData.tax_code}</div>}
              <div className={hasIssuer ? "issuer-unit" : undefined}>
                {receiptData.unit_label || "Đơn vị"}: {receiptData.organization}
              </div>
              {receiptData.code && <div>Mã QHNS: {receiptData.code}</div>}
            </div>
            {hasRightBlock && (
              <div className={`header-right${showNationalTitle ? " national" : ""}`}>
                {showNationalTitle && (
                  <>
                    <div className="national-title">CỘNG HÒA XÃ HỘI CHỦ NGHĨA VIỆT NAM</div>
                    <div className="national-title">
                      <span className="national-motto">Độc lập - Tự do - Hạnh phúc</span>
                    </div>
                  </>
                )}
                {showFormCode && (
                  <div style={showNationalTitle ? { marginTop: "8px" } : undefined}>
                    <div style={{ fontWeight: 700, marginBottom: "8px" }}>Mẫu số C45-BB</div>
                    <div style={{ fontSize: "10px", lineHeight: 1.3, color: "#666" }}>
                      (Ban hành theo TT số: 107/2017/TT-BTC ngày 10/10/2017 của Bộ Tài Chính)
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Title */}
          <div className="title-section">
            <div className="receipt-title">{receiptData.title || "PHIẾU THU"}</div>
            <div className="receipt-date">
              Ngày {date.day} tháng {date.month} năm {date.year}
            </div>
            <div className="receipt-number">Số: {receiptData.receipt_no}</div>
          </div>

          {/* Info Section */}
          <div className="info-section">
            <div className="info-item">
              <span className="info-label">Họ và tên người nộp tiền:</span>
              <span className="info-value">{receiptData.payer_name}</span>
            </div>
            {/* Họ tên và số tiền luôn hiện; các dòng khác theo cấu hình phiếu của chủ, như PDF */}
            {receiptData.show_address !== false && (
              <div className="info-item">
                <span className="info-label">{receiptData.address_label || "Địa chỉ"}:</span>
                <span className="info-value">{receiptData.address}</span>
              </div>
            )}
            {receiptData.show_content !== false && (
              <div className="info-item">
                <span className="info-label">Nội dung:</span>
                <Text
                  className="info-value"
                  copyable={{ text: receiptData.content }}
                >
                  {receiptData.content}
                </Text>
              </div>
            )}
            {receiptData.deadline && (
              <div className="info-item">
                <span className="info-label">Hạn thanh toán:</span>
                <span className="info-value">{receiptData.deadline}</span>
              </div>
            )}
          </div>

          {/* Amount Highlight */}
          <div className="amount-highlight">
            <div style={{ fontSize: "11px", color: "#666", marginBottom: "4px" }}>Số tiền</div>
            {/* Số còn phải nộp, khớp số tiền trên QR và phiếu PDF */}
            <div className="amount-value">{formatCurrency(receiptData.total)}</div>
            {receiptData.show_amount_text !== false && (
              <div className="amount-text">
                <strong>(Viết bằng chữ):</strong> {receiptData.total_text}
              </div>
            )}
          </div>

          {isSettled && (
            <Alert
              type={receiptData.status === "CANCELLED" ? "warning" : "success"}
              showIcon
              style={{ margin: "12px 0" }}
              message={settledMessage()}
            />
          )}

          {/* QR Code */}
          {receiptData.vietqr && (
            <div className="qr-section">
              <img src={receiptData.vietqr} alt="QR thanh toán" className="qr-image" />
              
              <div style={{ fontSize:"13px" }}>
                <h3>HƯỚNG DẪN THANH TOÁN</h3>
                <span>
                  Đăng nhập ứng dụng ngân hàng &gt; Chọn quét QR &gt; Kiểm tra &gt;
                  Xác nhận thanh toán
                </span>
                <br />
                <span>(Không điều chỉnh nội dung thanh toán)</span>
              </div>

              <div style={{ marginTop: 5 }}>
                <a
                  href={receiptData.vietqr}
                  download="qr-thanh-toan.png"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button icon={<DownloadOutlined />} style={{ fontSize: '12px' }}>
                    Tải QR Code
                  </Button>
                </a>
              </div>
            </div>
          )}

          {/* Details */}
          <div className="details-section">
            <div className="details-title">Chi tiết đợt thu: {receiptData.dotthu}</div>
            {receiptData.details.map((item, index) => (
              <div key={index} className="detail-row">
                <span className="detail-name">{index + 1}. {item.name}</span>
                <span className="detail-amount">{formatCurrency(item.amount)}</span>
              </div>
            ))}
          </div>

          {/* Summary */}
          <div className="summary-section">
            <div className="summary-row">
              <span className="summary-label">Tạm tính:</span>
              <span className="summary-value">{formatCurrency(receiptData.subtotal)}</span>
            </div>
            <div className="summary-row">
              <span className="summary-label">Giảm giá:</span>
              <span className="summary-value">{formatCurrency(receiptData.discount)}</span>
            </div>
            <div className="summary-row">
              <span className="summary-label">Đã thanh toán:</span>
              <span className="summary-value">{formatCurrency(receiptData.amount_paid)}</span>
            </div>
            <div className="summary-row summary-total">
              <span className="summary-label">Tổng cộng phải trả:</span>
              <span className="summary-value">{formatCurrency(receiptData.total)}</span>
            </div>
          </div>

          {/* Signature - hồ sơ không có ô ký (thông báo phí) thì bỏ cả khối, như phiếu PDF */}
          {receiptData.signatures?.length > 0 && (
            <div className="signature-section">
              {receiptData.signatures.map((signature) => (
                <div key={signature} className="signature-box">
                  <div className="signature-title">{signature}</div>
                  <div className="signature-line">(Ký, họ tên)</div>
                </div>
              ))}
            </div>
          )}

          {/* Ghi chú cuối phiếu theo cấu hình của chủ, giữ xuống dòng như phiếu PDF */}
          {receiptData.footer_note && <div className="footer-note">{receiptData.footer_note}</div>}
        </div>
      </div>
    </div>
  );
};

export default Receipt;